import React, { useEffect, useState, useRef } from 'react';
import { DownloadSimple, X, ShareNetwork, Plus, AppleLogo } from '@phosphor-icons/react';
import { trackConversion } from '../utils/analytics';

/**
 * PWAInstallPrompt
 * - Captures the Android/Chrome `beforeinstallprompt` event and shows a dismissible banner.
 * - On iOS Safari shows a one-time "Add to Home Screen" walkthrough (iOS won't auto-prompt).
 * - Respects user dismissal — won't pester. Cooldown stored in localStorage.
 * - Only renders for logged-in contractors and workers (the audience that benefits most).
 */
const DISMISS_KEY = 'ch_pwa_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const MIN_VISITS_KEY = 'ch_visit_count';
const MIN_VISITS = 2;

function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
}

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [userType, setUserType] = useState(null);
  const shownTracked = useRef(false);

  // Conversion tracking: prompt shown (once per mount)
  useEffect(() => {
    if (show && !shownTracked.current) {
      shownTracked.current = true;
      trackConversion('pwa_prompt_shown');
    }
  }, [show]);

  // Bump visit counter
  useEffect(() => {
    try {
      const c = parseInt(localStorage.getItem(MIN_VISITS_KEY) || '0', 10) + 1;
      localStorage.setItem(MIN_VISITS_KEY, String(c));
    } catch {}
    setUserType(localStorage.getItem('userType'));
  }, []);

  // Listen for beforeinstallprompt (Android / desktop Chrome / Edge)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      trackConversion('pwa_prompt_installed');
      setShow(false);
      setDeferredPrompt(null);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Decide whether to surface the banner
  useEffect(() => {
    if (!userType) return;
    if (userType !== 'contractor' && userType !== 'worker' && userType !== 'homeowner') return;
    if (isInStandaloneMode()) return;

    const visits = parseInt(localStorage.getItem(MIN_VISITS_KEY) || '0', 10);
    if (visits < MIN_VISITS) return;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // Android/Chrome: only show after we've captured the prompt event
    if (deferredPrompt) {
      setShow(true);
      return;
    }
    // iOS Safari: no native prompt — show only on iOS with at least 2 visits
    if (isIOSSafari()) {
      setShow(true);
    }
  }, [userType, deferredPrompt]);

  const dismiss = () => {
    setShow(false);
    setShowIOSGuide(false);
    trackConversion('pwa_prompt_dismissed');
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          trackConversion('pwa_prompt_installed');
          setShow(false);
        } else {
          dismiss();
        }
        setDeferredPrompt(null);
      } catch {
        dismiss();
      }
      return;
    }
    // iOS: open the guidance modal
    if (isIOSSafari()) {
      setShowIOSGuide(true);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Floating install banner (sits above the mobile tab bar) */}
      <div
        className="fixed bottom-[78px] left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60]"
        data-testid="pwa-install-prompt"
      >
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-2xl p-4 shadow-[0_12px_40px_rgba(15,23,42,0.18)]">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-11 h-11 bg-[#2563EB] rounded-xl flex items-center justify-center shadow-[0_6px_16px_rgba(37,99,235,0.35)]">
              <DownloadSimple size={22} weight="bold" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] mb-0.5">Install ConstructHelp</p>
              <h3 className="font-bold text-base leading-tight mb-1">One tap from your home screen</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Faster cold-starts, full-screen mode, push notifications. Built for life on a job site.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={handleInstall}
                  className="h-9 px-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-bold rounded-full transition-colors"
                  data-testid="pwa-install-accept-btn"
                >
                  {isIOSSafari() && !deferredPrompt ? 'Show Me How' : 'Install'}
                </button>
                <button
                  onClick={dismiss}
                  className="h-9 px-3 bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-white text-xs font-semibold rounded-full"
                  data-testid="pwa-install-dismiss-btn"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="flex-shrink-0 w-7 h-7 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded-full flex items-center justify-center"
              aria-label="Close install prompt"
              data-testid="pwa-install-close-btn"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Add-to-Home-Screen modal */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={dismiss}
          data-testid="pwa-ios-guide"
        >
          <div
            className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-[0_12px_40px_rgba(15,23,42,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AppleLogo size={22} weight="fill" className="text-white" />
                <h3 className="font-black text-lg">Add to iPhone Home Screen</h3>
              </div>
              <button
                onClick={dismiss}
                className="w-7 h-7 bg-neutral-800 hover:bg-neutral-700 rounded-md flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <ol className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-xs">1</span>
                <div className="flex-1">
                  <p className="font-bold">Tap the <ShareNetwork size={14} weight="bold" className="inline mb-0.5 text-[#2563EB]" /> Share button</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">At the bottom of Safari.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-xs">2</span>
                <div className="flex-1">
                  <p className="font-bold">Scroll & tap <Plus size={14} weight="bold" className="inline mb-0.5 text-[#2563EB]" /> "Add to Home Screen"</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">You may need to scroll the menu.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-xs">3</span>
                <div className="flex-1">
                  <p className="font-bold">Tap "Add" in the top-right</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">ConstructHelp will appear on your home screen.</p>
                </div>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-6 w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm rounded-full transition-colors"
              data-testid="pwa-ios-guide-close-btn"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
